import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { quranApi } from '../lib/api';

export default function TafsirSheet({ 
  isOpen, 
  onClose, 
  surahNo, 
  ayahNo,
  surahName 
}) {
  const [tafsir, setTafsir] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTafsir = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await quranApi.getVerseTafsir(surahNo, ayahNo);
        setTafsir(data);
      } catch (err) {
        console.error('Error loading tafsir:', err);
        setError('Failed to load tafsir. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen && surahNo && ayahNo) {
      loadTafsir();
    }
  }, [isOpen, surahNo, ayahNo]);

  // Simple markdown renderer
  const renderMarkdown = (text) => {
    if (!text) return null;
    
    // Convert markdown headers and basic formatting
    const html = text
      .replace(/^## (.*$)/gm, '<h2 class="text-lg font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^### (.*$)/gm, '<h3 class="text-base font-semibold mt-3 mb-2">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n\n/g, '</p><p class="mb-3">')
      .replace(/\n/g, '<br/>');
    
    return <div dangerouslySetInnerHTML={{ __html: `<p class="mb-3">${html}</p>` }} />;
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg" data-testid="tafsir-sheet">
        <SheetHeader className="border-b pb-4">
          <SheetTitle className="flex items-center justify-between">
            <div>
              <span className="text-lg font-serif">Tafsir</span>
              <p className="text-sm font-normal text-muted-foreground mt-1">
                {surahName} - Ayah {ayahNo}
              </p>
            </div>
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] mt-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="text-center py-12 text-destructive">
              <p>{error}</p>
              <button 
                onClick={loadTafsir}
                className="mt-2 text-primary underline"
              >
                Try again
              </button>
            </div>
          )}

          {tafsir && tafsir.tafsirs && (
            <Tabs defaultValue="0" className="w-full">
              <TabsList className="w-full grid grid-cols-3 mb-4">
                {tafsir.tafsirs.map((t, index) => (
                  <TabsTrigger 
                    key={index} 
                    value={index.toString()}
                    className="text-xs sm:text-sm"
                    data-testid={`tafsir-tab-${index}`}
                  >
                    {t.author}
                  </TabsTrigger>
                ))}
              </TabsList>

              {tafsir.tafsirs.map((t, index) => (
                <TabsContent key={index} value={index.toString()} className="mt-0">
                  {t.groupVerse && (
                    <div className="bg-muted/50 rounded-lg p-3 mb-4 text-sm text-muted-foreground">
                      {t.groupVerse}
                    </div>
                  )}
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    {renderMarkdown(t.content)}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
